import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchVector } from './api';
import { MAX_MINUTES } from './palette';
import type { FieldStats, GridSpec, Meta, Stations } from './types';
import type { ComputeEntry, FromWorker, ToWorker } from '../workers/protocol';
import FieldWorker from '../workers/field.worker?worker';

export interface FieldRequest extends ComputeEntry {
  dir: 'to' | 'from';
}

export interface FieldResult {
  values: Float32Array;
  width: number;
  height: number;
  stats: FieldStats;
  computeMs: number;
}

interface Options {
  meta: Meta | null;
  stations: Stations | null;
  grid: GridSpec;
  /** Called with raw texture data so the map layer can upload it. */
  onTexture: (data: Uint16Array, width: number, height: number) => void;
  onEmpty: () => void;
}

/**
 * Owns the field worker: keeps travel-time vectors loaded, coalesces recompute
 * requests, and hands finished textures to the map.
 */
export function useFieldEngine({ meta, stations, grid, onTexture, onEmpty }: Options) {
  const workerRef = useRef<Worker | null>(null);
  const loadedKeys = useRef(new Set<string>());
  const inFlightVectors = useRef(new Map<string, Promise<void>>());
  const reqCounter = useRef(0);
  const activeReq = useRef(0);
  const queued = useRef<FieldRequest[] | null>(null);
  const busy = useRef(false);
  /** Indirection so the worker's message handler can call the latest submit. */
  const submitRef = useRef<(requests: FieldRequest[]) => void>(() => {});

  const [result, setResult] = useState<FieldResult | null>(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tableInfo, setTableInfo] = useState<{ buildMs: number; pairs: number } | null>(null);

  // Keep the latest callbacks reachable without re-creating the worker.
  const onTextureRef = useRef(onTexture);
  const onEmptyRef = useRef(onEmpty);
  onTextureRef.current = onTexture;
  onEmptyRef.current = onEmpty;

  const post = useCallback((msg: ToWorker, transfer?: Transferable[]) => {
    workerRef.current?.postMessage(msg, transfer ?? []);
  }, []);

  const ensureVector = useCallback(
    async (req: FieldRequest) => {
      if (loadedKeys.current.has(req.key)) return;
      const existing = inFlightVectors.current.get(req.key);
      if (existing) return existing;
      const promise = (async () => {
        const buffer = await fetchVector(req.lat, req.lon, req.dir);
        loadedKeys.current.add(req.key);
        post({ type: 'setVector', key: req.key, buffer }, [buffer]);
      })()
        .catch((err) => {
          setError(err instanceof Error ? err.message : String(err));
          throw err;
        })
        .finally(() => {
          inFlightVectors.current.delete(req.key);
        });
      inFlightVectors.current.set(req.key, promise);
      return promise;
    },
    [post]
  );

  const submit = useCallback(
    async (requests: FieldRequest[]) => {
      if (!workerRef.current) return;
      if (!requests.length) {
        activeReq.current = ++reqCounter.current;
        setResult(null);
        onEmptyRef.current();
        setComputing(false);
        return;
      }
      busy.current = true;
      setComputing(true);
      try {
        await Promise.all(requests.map(ensureVector));
      } catch {
        busy.current = false;
        setComputing(false);
        return;
      }
      const reqId = ++reqCounter.current;
      activeReq.current = reqId;
      post({
        type: 'compute',
        reqId,
        entries: requests.map(({ key, lat, lon, weight }) => ({ key, lat, lon, weight })),
      });
    },
    [ensureVector, post]
  );
  submitRef.current = submit;

  /** Run the most recent queued request, if the worker just went idle. */
  const drain = useCallback(() => {
    const next = queued.current;
    if (!next || busy.current) return;
    queued.current = null;
    submitRef.current(next);
  }, []);

  /** Request a field; while one is running only the newest request is kept. */
  const request = useCallback(
    (requests: FieldRequest[]) => {
      if (busy.current) {
        queued.current = requests;
        return;
      }
      void submit(requests);
    },
    [submit]
  );

  // ---- worker lifecycle
  useEffect(() => {
    if (!meta || !stations) return;
    const worker = new FieldWorker();
    workerRef.current = worker;
    loadedKeys.current.clear();
    inFlightVectors.current.clear();

    worker.onmessage = (ev: MessageEvent<FromWorker>) => {
      const msg = ev.data;
      if (msg.type === 'ready') {
        setTableInfo({ buildMs: msg.buildMs, pairs: msg.pairs });
        return;
      }
      if (msg.type === 'error') {
        setError(msg.message);
        setComputing(false);
        busy.current = false;
        return;
      }
      if (msg.type === 'empty') {
        if (msg.reqId === activeReq.current) {
          setResult(null);
          onEmptyRef.current();
          setComputing(false);
        }
        busy.current = false;
        drain();
        return;
      }
      if (msg.reqId === activeReq.current) {
        onTextureRef.current(new Uint16Array(msg.texture), msg.width, msg.height);
        setResult({
          values: new Float32Array(msg.values),
          width: msg.width,
          height: msg.height,
          stats: msg.stats,
          computeMs: msg.computeMs,
        });
        setComputing(false);
        setError(null);
      }
      busy.current = false;
      drain();
    };

    worker.onerror = (ev) => {
      setError(ev.message || 'heatmap worker crashed');
      setComputing(false);
      busy.current = false;
    };

    worker.postMessage({
      type: 'init',
      stations,
      grid,
      config: {
        maxAccessWalkM: meta.maxAccessWalkM,
        walkSpeedMps: meta.walkSpeedMps,
        walkDetour: meta.walkDetour,
        unreachable: meta.unreachable,
        maxMinutes: MAX_MINUTES,
      },
    } satisfies ToWorker);

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
    // `grid` is intentionally omitted: changes are pushed via setGrid below so
    // the worker (and its loaded vectors) survive a resolution change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, stations, drain]);

  // ---- resolution changes rebuild the access table, keeping loaded vectors
  const gridRef = useRef(grid);
  useEffect(() => {
    if (!workerRef.current) return;
    if (gridRef.current === grid) return;
    gridRef.current = grid;
    post({ type: 'setGrid', grid });
  }, [grid, post]);

  return { request, result, computing, error, tableInfo, clearError: () => setError(null) };
}
